import React, { useEffect, useState } from "react";
import { ChevronLeft, Edit2 } from 'lucide-react';
import { useNavigate, useParams } from "react-router-dom";
import PaginationButtons from "../components/PaginationButtons";
import Modal from "../components/Modal"; // 🛑 Using your provided Modal component
import useBank from "../hooks/useBank";
import { toast } from 'react-toastify';






const BankPage = () => {
    const navigate = useNavigate();

    const [showBankModal, setShowBankModal] = useState(false);
    const [showAddMoneyModal, setShowAddMoneyModal] = useState(false);
    const [showSpendMoneyModal, setShowSpendMoneyModal] = useState(false);
    const [isEditingBank, setIsEditingBank] = useState(false);
    const [bank, setBankDetails] = useState([]);
    const [transactions, setTransaction] = useState([]);
    const [loadingTransaction, setLoadingTransaction] = useState(false);
    const { accountId } = useParams();

    const handleModalOpen = (setter, isEdit = false) => {
        setIsEditingBank(isEdit);
        setter(true);
    };
    const handleModalClose = (setter) => setter(false);
    const { getBankDetailsById, getBankTransaction, updateBank } = useBank();

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (showBankModal && isEditingBank) {
            const formData = new FormData(e.target);
            const data = {
                accountName: formData.get("accountName"),
                accountNumber: formData.get("accountNumber"),
                ifscCode: formData.get("ifscCode"),
                accountType: formData.get("accountType"),
            };

            try {
                const res = await updateBank(accountId, data);
                if (res.success) {
                    toast.success("Bank details updated successfully");
                    fetchBankDetailsById(); // Refresh data
                    setShowBankModal(false);
                } else {
                    toast.error(res.message || "Failed to update bank details");
                }
            } catch (err) {
                console.error(err);
                toast.error(err.message || "Something went wrong");
            }
        } else {
            // Placeholder for other modals or add mode
            console.log("Form Submitted - UI Only / Add Mode Pending");
            setShowBankModal(false);
            setShowAddMoneyModal(false);
            setShowSpendMoneyModal(false);
        }
    };

    const fetchBankDetailsById = async () => {
        try {
            const response = await getBankDetailsById(accountId);
            setBankDetails(response);
        } catch (e) {
            console.log("Error fetching bank details:", e);
        }
    }
    const fetchBankTransaction = async () => {
        setLoadingTransaction(true);
        try {
            const bankTransaction = await getBankTransaction(accountId);
            setTransaction(bankTransaction.data);
        } catch (e) {
            console.log("Error fetching bank details:", e);
        } finally {
            setLoadingTransaction(false);
        }
    }
    useEffect(() => {
        if (accountId) {
            fetchBankTransaction();
            fetchBankDetailsById();
        }
    }, [accountId]);
    const [currentPage, setCurrentPage] = useState(1);
    const [transactionsPerPage, setTransactionsPerPage] = useState(10);

    // Derived state for pagination
    const { totalTransactions, currentTransactions, totalPages } = React.useMemo(() => {
        const totalTransactions = transactions.length;
        const totalPages = Math.ceil(totalTransactions / transactionsPerPage);
        const lastIndex = currentPage * transactionsPerPage;
        const firstIndex = lastIndex - transactionsPerPage;
        const currentTransactions = transactions.slice(firstIndex, lastIndex);

        return { totalTransactions, currentTransactions, totalPages, firstIndex };
    }, [transactions, currentPage, transactionsPerPage]);

    // Reset to page 1 if items per page changes
    useEffect(() => {
        setCurrentPage(1);
    }, [transactionsPerPage]);

    return (
        <>
            <div className="bg-gray-50 min-h-screen  relative pt-4">
                {/* ... (Back Button) ... */}
                <div className="absolute -top-4 -left-4 md:-top-4 md:-left-6">
                    {/* Back Button */}
                    <button
                        onClick={() => navigate("/dashboard/accounts")}
                        className="mb-0 flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors group"
                    >
                        <ChevronLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                        <span className="font-medium">Back to Accounts</span>
                    </button>
                </div>

                {/* --- Main View --- */}
                <div className="flex flex-col p-2">
                    <div className="mb-8 grid grid-cols-1 md:grid-cols-4 gap-6">

                        {/* --- CARD 1: BANK DETAILS --- */}
                        <div className="md:col-span-2 bg-white shadow-lg rounded-lg p-5">
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-800">{bank.accountName}</h3>
                                    <p className="text-sm text-gray-500 capitalize">{bank.accountType} Account</p>
                                </div>
                                <button
                                    onClick={() => handleModalOpen(setShowBankModal, true)}
                                    className="flex items-center text-sm text-blue-600 hover:text-blue-800 font-medium"
                                >
                                    <Edit2 className="w-4 h-4 mr-1" /> Edit
                                </button>
                            </div>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-500 font-medium">A/C No:</span>
                                    <span className="font-mono text-gray-700 font-medium">{bank.accountNumber}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500 font-medium">IFSC:</span>
                                    <span className="font-mono text-gray-700 font-medium uppercase">{bank.ifscCode}</span>
                                </div>
                            </div>
                        </div>

                        {/* --- CARD 2: CURRENT BALANCE --- */}
                        <div className="bg-white shadow-lg rounded-lg p-5 flex flex-col justify-center text-center">
                            <p className="text-sm font-medium text-gray-500 uppercase">Available Balance</p>
                            <p className="text-4xl font-bold text-emerald-600">
                                {bank.balance?.toLocaleString('en-IN', {
                                    style: 'currency', currency: 'INR', maximumFractionDigits: 0
                                })}
                            </p>
                        </div>

                        {/* --- CARD 3 & 4: ACTION BUTTONS ---
                        <div className="flex flex-col space-y-4">
                            <button
                                onClick={() => handleModalOpen(setShowAddMoneyModal)}
                                className="w-full bg-green-600 text-white rounded-lg p-5 h-full flex items-center justify-center text-lg font-medium hover:bg-green-700 transition-colors"
                            >
                                Add Money
                            </button>
                            <button
                                onClick={() => handleModalOpen(setShowSpendMoneyModal)}
                                className="w-full bg-red-600 text-white rounded-lg p-5 h-full flex items-center justify-center text-lg font-medium hover:bg-red-700 transition-colors"
                            >
                                Spend Money
                            </button>
                        </div> */}

                        {/* --- CARD 5: DOWNLOAD REPORT BUTTON (Fixed to md:col-span-1) --- */}
                        <div className="md:col-span-1 flex">
                            <button
                                onClick={() => console.log("Download Report Clicked - UI Only")}
                                className="w-full bg-blue-600 text-white rounded-lg p-5 h-full flex items-center justify-center text-lg font-medium hover:bg-blue-700 transition-colors"
                            >
                                Download Report
                            </button>
                        </div>
                    </div>

                    {/* --- TRANSACTION HISTORY TABLE --- */}
                    <div className="flex justify-between items-center mb-3">
                        <h2 className="text-xl font-semibold text-gray-800">Transaction History</h2>
                        <div className='flex items-center gap-3 text-gray-600 border border-gray-100 shadow-sm rounded-sm p-1 bg-white'>
                            <p className='text-sm px-2'>Rows per page</p>
                            <select
                                className="outline-none bg-transparent font-medium"
                                value={transactionsPerPage}
                                onChange={(e) => setTransactionsPerPage(Number(e.target.value))}
                            >
                                <option value='10'>10</option>
                                <option value='20'>20</option>
                                <option value='50'>50</option>
                                <option value={totalTransactions > 0 ? totalTransactions : 9999}>All</option>
                            </select>
                        </div>
                    </div>

                    <div className="p-0">
                        <div className="w-full bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
                            <div className="overflow-x-auto">
                                <table className="w-full divide-y divide-gray-200">
                                    <thead className="sticky top-0 bg-gray-100 text-gray-700">
                                        <tr>
                                            <th scope="col" className="py-3 px-4 text-xs font-semibold tracking-wider text-left">SR.NO</th>
                                            <th scope="col" className="py-3 px-4 text-xs font-semibold tracking-wider text-left min-w-[120px]">NAME</th>
                                            <th scope="col" className="py-3 px-4 text-xs font-semibold tracking-wider text-left min-w-[100px]">DATE</th>
                                            <th scope="col" className="py-3 px-4 text-xs font-semibold tracking-wider text-left min-w-[100px]">Bill No</th>
                                            <th scope="col" className="py-3 px-4 text-xs font-semibold tracking-wider text-left min-w-[100px]">ORIGIN</th>
                                            <th scope="col" className="py-3 px-4 text-xs font-semibold tracking-wider text-left">TYPE</th>
                                            <th scope="col" className="py-3 px-4 text-xs font-semibold tracking-wider text-left min-w-[120px]">AMOUNT</th>
                                            <th scope="col" className="py-3 px-4 text-xs font-semibold tracking-wider text-left">STATUS</th>
                                            <th scope="col" className="py-3 px-4 text-xs font-semibold tracking-wider text-left min-w-[150px]">DESCRIPTION</th>
                                            <th scope="col" className="py-3 px-4 text-xs font-semibold tracking-wider text-left min-w-[80px]">ACTIONS</th>
                                        </tr>
                                    </thead>

                                    <tbody className="divide-y divide-gray-200 bg-white">
                                        {loadingTransaction ? (
                                            <tr>
                                                <td colSpan="10" className="py-10 text-center">
                                                    <div className="flex justify-center">
                                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                                                    </div>
                                                    <p className="text-gray-400 mt-2 text-sm">Loading Transactions...</p>
                                                </td>
                                            </tr>
                                        ) : transactions.length === 0 ? (
                                            <tr>
                                                <td colSpan="10" className="py-10 text-center text-gray-400 italic">No transactions found.</td>
                                            </tr>
                                        ) : (
                                            currentTransactions.map((data, index) => {
                                                // Calculate global index for Sr.No
                                                const globalIndex = (currentPage - 1) * transactionsPerPage + index + 1;
                                                const isCancelled = data.status === 'Cancelled';
                                                return (
                                                    <tr key={data._id} className={`hover:bg-gray-50 transition-colors duration-150 ${isCancelled ? 'opacity-60 bg-gray-50' : ''}`}>
                                                        <td className="py-3 px-4 text-sm text-gray-500">{globalIndex}</td>
                                                        <td className={`py-3 px-4 text-sm font-medium text-gray-900 whitespace-nowrap ${isCancelled ? 'line-through decoration-gray-400' : ''}`}>
                                                            {data.name || <span className="text-gray-400">N/A</span>}
                                                        </td>
                                                        <td className={`py-3 px-4 text-sm text-gray-700 whitespace-nowrap ${isCancelled ? 'line-through decoration-gray-400' : ''}`}>
                                                            {new Date(data.date).toLocaleDateString('en-GB')}
                                                        </td>
                                                        <td className={`py-3 px-4 text-sm font-medium text-gray-900 whitespace-nowrap ${isCancelled ? 'line-through decoration-gray-400' : ''}`}>
                                                            {data.billNo || <span className="text-gray-400">N/A</span>}
                                                        </td>
                                                        <td className="py-3 px-4">
                                                            <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold bg-blue-700 text-gray-200`}>
                                                                {data.origin}
                                                            </span>
                                                        </td>
                                                        <td className="py-3 px-4">
                                                            <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${data.type === 'CREDIT' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                                }`}>
                                                                {data.type}
                                                            </span>
                                                        </td>
                                                        <td className={`py-3 px-4 text-sm font-bold whitespace-nowrap ${data.type === 'CREDIT' ? 'text-green-600' : 'text-red-600'} ${isCancelled ? 'line-through decoration-gray-400' : ''}`}>
                                                            {data.amount.toLocaleString('en-IN', {
                                                                style: 'currency', currency: 'INR', maximumFractionDigits: 0
                                                            })}
                                                        </td>
                                                        <td className="py-3 px-4">
                                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border
                                                                    ${data.status === 'Completed' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' :
                                                                    data.status === 'Pending' ? 'bg-amber-50 text-amber-700 border-amber-100' :
                                                                        data.status === 'Cancelled' ? 'bg-gray-100 text-gray-500 border-gray-200' :
                                                                            data.status === 'Failed' ? 'bg-red-50 text-red-700 border-red-100' :
                                                                                'bg-blue-50 text-blue-700 border-blue-100' // Bounced or Default
                                                                }`}>
                                                                {data.status || 'Completed'}
                                                            </span>
                                                        </td>
                                                        <td className={`py-3 px-4 text-sm text-gray-500 max-w-[200px] truncate ${isCancelled ? 'line-through decoration-gray-400' : ''}`}>{data.description}</td>
                                                        <td className='py-3 px-4 text-center'>
                                                            <button
                                                                onClick={() => console.log("Download Single PDF Clicked - UI Only")}
                                                                className='px-3 py-1 text-xs text-white bg-gray-700 hover:bg-gray-800 rounded-full'
                                                            >
                                                                PDF
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination Navigation */}
                            <div className="border-t border-gray-200 bg-gray-50 p-2">
                                <PaginationButtons
                                    totalRecord={totalTransactions}
                                    recordPerPage={transactionsPerPage}
                                    currentPage={currentPage}
                                    setCurrentPage={(page) => setCurrentPage(page)}
                                    setNextPage={() => setCurrentPage((prev) => (Math.min(totalPages, prev + 1)))}
                                    setPrevPage={() => setCurrentPage((prev) => (Math.max(1, prev - 1)))}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* --- MODALS --- */}

            {/* 1. Bank Add/Edit Modal */}
            <Modal
                isOpen={showBankModal}
                onClose={() => handleModalClose(setShowBankModal)}
                title={isEditingBank ? "Edit Bank Account" : "Add New Bank Account"}
            >
                <form onSubmit={handleSubmit} className="mt-4 space-y-4">
                    <label htmlFor="accountName" className="block text-sm font-medium text-gray-700">Account Name</label>
                    <input type="text" name="accountName" className="w-full border border-gray-300 rounded-lg px-3 py-2 modal-form-input" placeholder="e.g. HDFC Bank" defaultValue={isEditingBank ? bank.accountName : ''} required />

                    <label htmlFor="accountNumber" className="block text-sm font-medium text-gray-700">Account Number</label>
                    <input type="text" name="accountNumber" maxLength={18} className="w-full border border-gray-300 rounded-lg px-3 py-2 modal-form-input" placeholder="Enter Account Number" defaultValue={isEditingBank ? bank.accountNumber : ''} required />

                    <label htmlFor="ifscCode" className="block text-sm font-medium text-gray-700">IFSC CODE</label>
                    <input type="text" name="ifscCode" maxLength={11} className="w-full border border-gray-300 rounded-lg px-3 py-2 modal-form-input" placeholder="e.g. HDFC0001234" defaultValue={isEditingBank ? bank.ifscCode : ''} required />

                    <label htmlFor="accountType" className="block text-sm font-medium text-gray-700">Account Type</label>
                    <select name="accountType" className="w-full border border-gray-300 rounded-lg px-3 py-2 modal-form-input bg-white" defaultValue={isEditingBank ? bank.accountType : 'Saving'} required>
                        <option value="Saving">Saving</option>
                        <option value="Current">Current</option>
                    </select>

                    <div className="pt-4 flex justify-end">
                        <button type="button" onClick={() => handleModalClose(setShowBankModal)} className="px-4 py-2 mr-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors">Cancel</button>
                        <button type="submit" className={`px-4 py-2 rounded-lg text-white transition-colors ${isEditingBank ? "bg-green-500 hover:bg-green-600" : "hover:bg-black bg-gray-800"}`}>
                            {isEditingBank ? "Save Details" : "Add Account"}
                        </button>
                    </div>
                </form>
            </Modal>

            {/* 2. Add Money Modal */}
            <Modal
                isOpen={showAddMoneyModal}
                onClose={() => handleModalClose(setShowAddMoneyModal)}
                title={"Add Money (Credit)"}
            >
                <form onSubmit={handleSubmit} className="mt-4 space-y-4">
                    <label htmlFor="amount" className="block text-sm font-medium text-gray-700">Amount</label>
                    <input type="number" name="amount" className="w-full border border-gray-300 rounded-lg px-3 py-2 modal-form-input" placeholder="0.00" required />

                    <label htmlFor="description" className="block text-sm font-medium text-gray-700">Description</label>
                    <input type="text" name="description" className="w-full border border-gray-300 rounded-lg px-3 py-2 modal-form-input" placeholder="Source of income" />

                    <div className="pt-4 flex justify-end">
                        <button type="button" onClick={() => handleModalClose(setShowAddMoneyModal)} className="px-4 py-2 mr-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors">Cancel</button>
                        <button type="submit" className="px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white transition-colors">Add Credit</button>
                    </div>
                </form>
            </Modal>

            {/* 3. Spend Money Modal */}
            <Modal
                isOpen={showSpendMoneyModal}
                onClose={() => handleModalClose(setShowSpendMoneyModal)}
                title={"Spend Money (Debit)"}
            >
                <form onSubmit={handleSubmit} className="mt-4 space-y-4">
                    <label htmlFor="amount" className="block text-sm font-medium text-gray-700">Amount</label>
                    <input type="number" name="amount" className="w-full border border-gray-300 rounded-lg px-3 py-2 modal-form-input" placeholder="0.00" required />

                    <label htmlFor="description" className="block text-sm font-medium text-gray-700">Description</label>
                    <input type="text" name="description" className="w-full border border-gray-300 rounded-lg px-3 py-2 modal-form-input" placeholder="Purpose of expense" />

                    <div className="pt-4 flex justify-end">
                        <button type="button" onClick={() => handleModalClose(setShowSpendMoneyModal)} className="px-4 py-2 mr-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors">Cancel</button>
                        <button type="submit" className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors">Add Debit</button>
                    </div>
                </form>
            </Modal>
        </>
    );
};

export default BankPage;